import React, { useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  Typography,
  Button,
  Spinner,
  Textarea,
} from "@material-tailwind/react";
import { 
  BellAlertIcon, 
  ChatBubbleLeftRightIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { colors } from "../../utils/colors";
import axiosInstance from "../../utils/axiosInstance";
import { trackEvent } from "../../utils/analytics";

export default function RelaunchModal({ isOpen, onClose, propertyId }) {
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await axiosInstance.post(`/documents/${propertyId}/relaunch`, {
        message: message.trim()
      });
      trackEvent('diagnostic_relaunch_teams', { 
        propertyId,
        messageProvided: message.trim() !== ''
      });
      setSuccess(true);
    } catch (error) {
      console.error('Erreur lors de la relance:', error);
      if (error.response?.status === 429) {
        const { hoursRemaining, minutesRemaining } = error.response.data;
        let timeMessage = '';
        if (hoursRemaining > 0) {
          timeMessage += `${hoursRemaining} heure${hoursRemaining > 1 ? 's' : ''}`;
        }
        if (minutesRemaining > 0) {
          if (timeMessage) timeMessage += ' et ';
          timeMessage += `${minutesRemaining} minute${minutesRemaining > 1 ? 's' : ''}`;
        }
        setError(`Vous pourrez relancer nos équipes dans ${timeMessage}`);
      } else {
        if (error.response?.data?.error) {
          setError(error.response?.data?.error);
        } else {
          setError("Une erreur est survenue lors de la relance. Veuillez réessayer plus tard.");
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Réinitialiser les états après la fermeture
    setTimeout(() => {
      setMessage("");
      setError(null);
      setSuccess(false);
    }, 300);
  };

  return (
    <Dialog open={isOpen} handler={handleClose} size="md">
      <DialogHeader className="justify-center border-b border-gray-200 pb-4">
        <div className="flex items-center justify-center gap-2">
          <BellAlertIcon className="h-6 w-6" style={{ color: colors.dimoPrimary }} />
          <Typography variant="h5" className="text-center">
            Relancer nos équipes
          </Typography>
        </div>
      </DialogHeader>
      <DialogBody className="text-center py-6">
        {success ? (
          <div className="space-y-6">
            <div className="bg-white border border-green-500 rounded-lg p-6 flex flex-col items-center space-y-4">
              <div className="bg-green-50 p-3 rounded-full">
                <BellAlertIcon className="h-8 w-8 text-green-500" />
              </div>
              <Typography variant="h6" className="text-green-700 font-medium">
                Demande de relance envoyée
              </Typography>
              <Typography variant="small" className="text-green-700 max-w-md">
                Nous avons bien reçu votre demande de relance. Nos équipes vont traiter votre dossier en priorité.
              </Typography>
            </div>
            <Button
              onClick={handleClose}
              style={{ backgroundColor: colors.dimoPrimary }}
              className="mt-4"
            >
              Fermer
            </Button>
          </div>
        ) : error ? (
          <div className="space-y-6">
            <div className="bg-white border border-red-500 rounded-lg p-6 flex flex-col items-center space-y-4">
              <div className="bg-red-50 p-3 rounded-full">
                <ClockIcon className="h-8 w-8 text-red-500" />
              </div>
              <Typography variant="h6" className="text-red-700 font-medium">
                Relance impossible pour le moment
              </Typography>
              <Typography variant="small" className="text-red-700 max-w-md">
                {error}
              </Typography>
            </div>
            <Button
              onClick={handleClose}
              style={{ backgroundColor: colors.dimoPrimary }}
              className="mt-4"
            >
              Fermer
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <ChatBubbleLeftRightIcon className="h-5 w-5" style={{ color: colors.dimoPrimary }} />
                <Typography variant="h6" className="text-gray-900">
                  Message pour nos équipes
                </Typography>
              </div>
              <Typography variant="small" className="text-gray-600 mb-4">
                Vous pouvez laisser un message optionnel pour nos équipes concernant votre demande de relance. Cela nous aidera à mieux comprendre vos besoins.
              </Typography>
              <Textarea
                label="Votre message (optionnel)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full"
                rows={4}
              />
            </div>
            <div className="flex gap-4 justify-center">
              <Button
                variant="outlined"
                onClick={handleClose}
                style={{ borderColor: colors.dimoPrimary, color: colors.dimoPrimary }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                style={{ backgroundColor: colors.dimoPrimary }}
                disabled={isSubmitting}
                className="flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Spinner className="h-4 w-4" /> Envoi en cours...
                  </>
                ) : (
                  <>
                    <BellAlertIcon className="h-4 w-4" /> Envoyer la relance
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
} 